import { useCallback, useEffect, useState } from 'react'
import { getPartnerWithSyncLifecycleStatuses, getSyncLifecycleStatus } from 'api/partners/syncLifecycle'
import { 
  SyncLifecycleStatusResource,
  SyncLifecycleState,
  SyncLifecycleStatusAttributes 
} from 'api/models'
import { StepProgress } from 'components/SyncProgressBar/SyncProgressBar'

type UseSyncLifecycleReturn = {
  // Latest active sync data
  activeSyncStatus: SyncLifecycleStatusAttributes | null
  activeSyncId: string | null
  
  // All sync statuses for table display
  allSyncStatuses: SyncLifecycleStatusResource[]
  
  // Progress bar data
  progressSteps: StepProgress[]
  isSyncing: boolean
  elapsedTime: string
  
  // State management
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

// Helper to map sync lifecycle state to progress steps
const mapSyncStateToProgressSteps = (
  syncStatus: SyncLifecycleStatusAttributes | null
): StepProgress[] => {
  if (!syncStatus) {
    return [
      { state: 'pending' },
      { state: 'pending' },
      { state: 'pending' },
      { state: 'pending' },
    ]
  }

  const steps: StepProgress[] = []
  const state = syncStatus.aasm_state

  // Import step
  if (syncStatus.import_started_at) {
    steps.push({
      state: syncStatus.import_completed_at ? 'complete' : 'active',
      status: syncStatus.import_completed_at ? 'success' : undefined,
      completedAt: syncStatus.import_completed_at ? new Date(syncStatus.import_completed_at) : undefined
    })
  } else {
    steps.push({ state: state === SyncLifecycleState.FAILED ? 'failed' : 'pending' })
  }

  // Ingest step - handle missing ingest fields gracefully
  if (syncStatus.ingest_started_at) {
    steps.push({
      state: syncStatus.ingest_completed_at ? 'complete' : 'active',
      status: syncStatus.ingest_completed_at ? 'success' : undefined,
      completedAt: syncStatus.ingest_completed_at ? new Date(syncStatus.ingest_completed_at) : undefined
    })
  } else if (syncStatus.import_completed_at && syncStatus.publish_started_at) {
    // Import done, publish started → assume ingest completed
    steps.push({ 
      state: 'complete', 
      status: 'success',
      completedAt: new Date(syncStatus.publish_started_at) // Use publish start as proxy
    })
  } else if (syncStatus.import_completed_at) {
    // Import done but publish not started → ingest in progress or pending
    steps.push({ state: 'active' })
  } else {
    steps.push({ state: state === SyncLifecycleState.FAILED ? 'failed' : 'pending' })
  }

  // Publish step
  if (syncStatus.publish_started_at) {
    steps.push({
      state: syncStatus.publish_completed_at ? 'complete' : 'active',
      status: syncStatus.publish_completed_at ? 'success' : undefined,
      completedAt: syncStatus.publish_completed_at ? new Date(syncStatus.publish_completed_at) : undefined
    })
  } else if (syncStatus.import_completed_at) {
    // Import done but publish not started → publish pending
    steps.push({ state: 'pending' })
  } else {
    steps.push({ state: state === SyncLifecycleState.FAILED ? 'failed' : 'pending' })
  }

  // 3rd Party Processing step
  if (syncStatus.external_processing_started_at) {
    steps.push({
      state: syncStatus.external_processing_completed_at ? 'complete' : 'active',
      status: syncStatus.external_processing_completed_at ? 'success' : undefined,
      completedAt: syncStatus.external_processing_completed_at ? new Date(syncStatus.external_processing_completed_at) : undefined
    })
  } else if (syncStatus.publish_completed_at) {
    steps.push({ state: 'pending' })
  } else {
    steps.push({ state: state === SyncLifecycleState.FAILED ? 'failed' : 'pending' })
  }

  // Handle failure state
  if (state === SyncLifecycleState.FAILED) {
    const lastActiveStepIndex = steps.map((step: StepProgress, index: number) => 
      step.state === 'active' ? index : -1
    ).filter(index => index >= 0).pop() ?? -1
    if (lastActiveStepIndex >= 0) {
      steps[lastActiveStepIndex] = { 
        ...steps[lastActiveStepIndex], 
        state: 'failed', 
        status: 'failure' 
      }
    }
  }

  // Set final status for completed sync
  if (state === SyncLifecycleState.COMPLETED && steps[steps.length - 1]?.state === 'complete') {
    steps[steps.length - 1].status = 'success'
  }

  return steps
}

// Helper to calculate elapsed time
const calculateElapsedTime = (syncStatus: SyncLifecycleStatusAttributes | null): string => {
  if (!syncStatus?.import_started_at) return ''
  
  const startTime = new Date(syncStatus.import_started_at).getTime()
  const endTime = syncStatus.ending_timestamp 
    ? new Date(syncStatus.ending_timestamp).getTime()
    : Date.now()
  
  const diff = endTime - startTime
  const totalSeconds = Math.floor(diff / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  
  return `${minutes}m ${seconds}s`
}

const useSyncLifecycle = (partnerId: string | null): UseSyncLifecycleReturn => {
  const [activeSyncStatus, setActiveSyncStatus] = useState<SyncLifecycleStatusAttributes | null>(null)
  const [activeSyncId, setActiveSyncId] = useState<string | null>(null)
  const [allSyncStatuses, setAllSyncStatuses] = useState<SyncLifecycleStatusResource[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchSyncData = useCallback(async () => {
    if (!partnerId) return

    setIsLoading(true)
    setError(null)

    try {
      // First, try to get active syncs (more efficient)
      const activeResponse = await getPartnerWithSyncLifecycleStatuses(partnerId, 'active')
      
      const activeSyncStatuses = activeResponse.included?.filter(
        (item): item is SyncLifecycleStatusResource => 
          item.type === 'sync_lifecycle_status'
      ) || []

      if (activeSyncStatuses.length > 0) {
        // Found active syncs - get the latest one
        const latestActiveSync = activeSyncStatuses.sort((a: SyncLifecycleStatusResource, b: SyncLifecycleStatusResource) => 
          new Date(b.attributes.created_at).getTime() - new Date(a.attributes.created_at).getTime()
        )[0]

        setActiveSyncId(latestActiveSync.id)
        
        // Fetch detailed status for the active sync
        const detailedResponse = await getSyncLifecycleStatus(latestActiveSync.id)
        setActiveSyncStatus(detailedResponse.data.attributes)
      } else {
        // No active syncs
        setActiveSyncId(null)
        setActiveSyncStatus(null)
      }

      // Also fetch all syncs for table display (recent completed + any active)
      const allResponse = await getPartnerWithSyncLifecycleStatuses(partnerId)
      const allStatuses = allResponse.included?.filter(
        (item): item is SyncLifecycleStatusResource => 
          item.type === 'sync_lifecycle_status'
      ) || []

      setAllSyncStatuses(allStatuses.sort((a: SyncLifecycleStatusResource, b: SyncLifecycleStatusResource) => 
        new Date(b.attributes.created_at).getTime() - new Date(a.attributes.created_at).getTime()
      ))

    } catch (err) {
      console.error('Error fetching sync lifecycle data:', err)
      setError('Failed to fetch sync status')
      setActiveSyncStatus(null)
      setActiveSyncId(null)
      setAllSyncStatuses([])
    } finally {
      setIsLoading(false)
    }
  }, [partnerId])

  useEffect(() => {
    fetchSyncData()
  }, [fetchSyncData])

  // Calculate derived state
  const progressSteps = mapSyncStateToProgressSteps(activeSyncStatus)
  const isSyncing = activeSyncStatus ? 
    [SyncLifecycleState.STARTED, SyncLifecycleState.IMPORT_STARTED, SyncLifecycleState.INGEST_STARTED, SyncLifecycleState.PUBLISH_STARTED, SyncLifecycleState.EXTERNAL_PROCESSING_STARTED].includes(activeSyncStatus.aasm_state) :
    false
  const elapsedTime = calculateElapsedTime(activeSyncStatus)

  return {
    activeSyncStatus,
    activeSyncId,
    allSyncStatuses,
    progressSteps,
    isSyncing,
    elapsedTime,
    isLoading,
    error,
    refetch: fetchSyncData
  }
}
export default useSyncLifecycle